import { Award, Star, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useDashboardData } from "@/hooks/useDashboardData";
import { ErrorCard } from "../layout/ErrorCard";

interface TopEmployeesWidgetProps {
  isLoading: boolean;
  error: any;
  onRetry: () => void;
}

export const TopEmployeesWidget = ({ isLoading, error, onRetry }: TopEmployeesWidgetProps) => {
  const { data: dashboardData } = useDashboardData();
  
  // استخراج أفضل الموظفين من بيانات لوحة التحكم
  const topEmployees = dashboardData?.sales?.topEmployees || [];

  return (
    <section>
      <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
        <Award className="w-5 h-5" />
        ترتيب أفضل الموظفين
        {isLoading && <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />}
      </h2>
      {error ? (
        <ErrorCard onRetry={onRetry} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {topEmployees.slice(0, 6).map((employee: any, index: number) => (
            <Card key={employee.name || index} className="bg-white/80 backdrop-blur-sm border border-slate-200 hover:border-slate-300 transition-all duration-300 hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold">
                          {employee.name ? employee.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2) : 'غ'}
                        </AvatarFallback>
                      </Avatar>
                      {index < 3 && (
                        <div className={`absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? 'bg-yellow-500 text-white' : 
                          index === 1 ? 'bg-gray-400 text-white' : 
                          'bg-amber-600 text-white'
                        }`}>
                          {index + 1}
                        </div>
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-base text-slate-800">{employee.name || 'غير محدد'}</CardTitle>
                      <p className="text-sm text-slate-600">{employee.department || 'قسم غير محدد'}</p>
                    </div>
                  </div>
                  {index < 3 && (
                    <Star className={`w-5 h-5 ${
                      index === 0 ? 'text-yellow-500 fill-yellow-500' : 
                      index === 1 ? 'text-gray-400 fill-gray-400' : 
                      'text-amber-600 fill-amber-600'
                    }`} />
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">إجمالي العروض</span>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                      {employee.totalOffers || 0}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">الإيرادات</span>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="font-bold text-green-600">
                        {employee.totalRevenue?.toLocaleString() || 0} ر.س
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">معدل النجاح</span>
                    <span className="font-bold text-slate-800">
                      {employee.successRate ? `${employee.successRate}%` : '0%'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {topEmployees.length === 0 && (
            <Card className="col-span-full bg-slate-50 border-dashed border-2 border-slate-300">
              <CardContent className="flex flex-col items-center justify-center p-8">
                <Award className="w-12 h-12 text-slate-400 mb-3" />
                <p className="text-slate-500 text-center">
                  لا توجد بيانات موظفين متاحة حالياً
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </section>
  );
};